import { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

type Step = 1 | 2 | 3;

const API_URL = 'https://script.google.com/macros/s/AKfycbyg_Sk9P24UR9ISko3TnWe4j3JxkmTaC5CijbgG2ERXakoU1NsM3tcZxJhaQj3N1m3g/exec';

const CheckPage = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<Step>(1);

  // Step 1: 基本情報
  const [age, setAge] = useState('');
  const [gender, setGender] = useState('');
  const [weight, setWeight] = useState('');
  const [alcohol, setAlcohol] = useState('');

  // Step 2: 血液データ
  const [ast, setAst] = useState('');
  const [alt, setAlt] = useState('');
  const [plate, setPlate] = useState('');

  // Step 3: 同意
  const [consent, setConsent] = useState(false);

  // 送信状態
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleNext = () => {
    if (currentStep < 3) {
      setCurrentStep((currentStep + 1) as Step);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep((currentStep - 1) as Step);
    }
  };

  // UUID生成関数
  const generateUUID = (): string => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  };

  const handleSubmit = async () => {
    // バリデーション
    if (!consent) {
      setError('利用規約・注意事項に同意してください');
      return;
    }

    // ✅ 型変換を明示的に Number() で実行
    const ageNum = Number(age);
    const astNum = Number(ast);
    const altNum = Number(alt);
    const plateNum = Number(plate);
    const weightNum = Number(weight);

    // ✅ 数値の検証を強化
    if (!ageNum || isNaN(ageNum) || ageNum <= 0) {
      setError('年齢を正しく入力してください');
      return;
    }
    if (!astNum || isNaN(astNum) || astNum <= 0) {
      setError('AST(GOT)を正しく入力してください');
      return;
    }
    if (!altNum || isNaN(altNum) || altNum <= 0) {
      setError('ALT(GPT)を正しく入力してください');
      return;
    }
    if (!plateNum || isNaN(plateNum) || plateNum <= 0) {
      setError('血小板数を正しく入力してください');
      return;
    }
    if (!weightNum || isNaN(weightNum) || weightNum <= 0) {
      setError('体重を正しく入力してください');
      return;
    }

    setIsSubmitting(true);
    setError('');

    // ✅ UUID を先に生成
    const uid = generateUUID();

    // ✅ 送信データ（型を明示的に確認）
    const payload = {
      action: 'submit_fib4',
      age: ageNum,        // ✅ 数値型
      gender: gender,     // ✅ 文字列型
      weight: weightNum,  // ✅ 数値型
      alcohol: alcohol,   // ✅ 文字列型
      ast: astNum,        // ✅ 数値型
      alt: altNum,        // ✅ 数値型
      plate: plateNum,    // ✅ 数値型（万/μL単位）
      uid: uid,           // ✅ 文字列型（UUID）
      consent: true,      // ✅ 真偽値型
    };

    console.log('📤 送信データ:', payload);
    console.log('📤 データ型確認:', {
      age: typeof payload.age,
      gender: typeof payload.gender,
      weight: typeof payload.weight,
      alcohol: typeof payload.alcohol,
      ast: typeof payload.ast,
      alt: typeof payload.alt,
      plate: typeof payload.plate,
      uid: typeof payload.uid,
      consent: typeof payload.consent,
    });
    console.log('📤 送信先URL:', API_URL);

    try {
      const response = await fetch(API_URL, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'text/plain',
        },
        body: JSON.stringify(payload),
      });

      console.log('📡 レスポンスステータス:', response.status);
      console.log('📡 レスポンスヘッダー:', {
        contentType: response.headers.get('content-type'),
        status: response.status,
        statusText: response.statusText,
      });

      // レスポンステキストを取得
      const responseText = await response.text();
      console.log('📥 レスポンステキスト（生データ）:', responseText);

      // JSONパース
      let result;
      try {
        result = JSON.parse(responseText);
        console.log('📥 パース済みデータ:', result);
      } catch (parseError) {
        console.error('❌ JSONパースエラー:', parseError);
        console.error('❌ パースエラー詳細:', {
          name: parseError instanceof Error ? parseError.name : 'Unknown',
          message: parseError instanceof Error ? parseError.message : String(parseError),
          stack: parseError instanceof Error ? parseError.stack : 'No stack trace',
        });
        console.error('❌ パースできなかった生データ:', responseText);
        setError('サーバーからの応答が不正です。しばらくしてから再度お試しください。');
        setIsSubmitting(false);
        return;
      }

      // ✅ GAS仕様に合わせた判定：errorフィールドの有無で判定
      if (result.error) {
        console.error('❌ GASエラー:', result.error);
        console.error('❌ エラーメッセージ:', result.message || '');
        setError(result.message || result.error || '送信に失敗しました。しばらくしてから再度お試しください。');
        setIsSubmitting(false);
        return;
      }

      // ✅ 成功時：fib4とriskの存在確認
      if (typeof result.fib4 === 'undefined' || typeof result.risk === 'undefined') {
        console.error('❌ 不完全なレスポンス:', result);
        setError('サーバーからの応答が不完全です。しばらくしてから再度お試しください。');
        setIsSubmitting(false);
        return;
      }

      console.log('✅ 送信成功！結果ページへ遷移します');
      console.log('✅ FIB-4:', result.fib4);
      console.log('✅ リスク:', result.risk);

      // 結果ページへ遷移
      navigate('/result', {
        state: {
          fib4: result.fib4,
          risk: result.risk,
          uid: payload.uid,
          inputData: {
            age,
            gender,
            weight,
            alcohol,
            ast,
            alt,
            plate,
          },
        },
      });
    } catch (err) {
      console.error('❌ 送信エラー（catchブロック）:', err);
      console.error('❌ エラーの型:', typeof err);
      console.error('❌ エラーの詳細:', {
        name: err instanceof Error ? err.name : 'Unknown',
        message: err instanceof Error ? err.message : String(err),
        stack: err instanceof Error ? err.stack : 'No stack trace',
        raw: err,
      });
      
      // エラーの詳細情報を表示
      if (err instanceof TypeError && err.message.includes('fetch')) {
        console.error('❌ ネットワークエラー: fetchが失敗しました');
        setError('ネットワークエラー: APIに接続できませんでした。インターネット接続を確認してください。');
      } else if (err instanceof TypeError) {
        console.error('❌ TypeError:', err.message);
        setError(`通信エラー: ${err.message}`);
      } else if (err instanceof Error) {
        console.error('❌ エラーメッセージ:', err.message);
        setError(`送信エラー: ${err.message}`);
      } else {
        console.error('❌ 不明なエラー:', err);
        setError('送信に失敗しました。しばらくしてから再度お試しください。');
      }
      
      setIsSubmitting(false);
    }
  };

  const isStep1Valid = age && gender && weight && alcohol;
  const isStep2Valid = ast && alt && plate;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-3 cursor-pointer"
          >
            <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-blue-500 to-green-500 rounded-lg">
              <i className="ri-heart-pulse-line text-white text-xl"></i>
            </div>
            <span className="text-xl font-bold text-gray-900">Liver Health Check</span>
          </button>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 pt-32 pb-20">
        {/* Progress Bar */}
        <div className="mb-12">
          <div className="flex items-center justify-between mb-4">
            {[1, 2, 3].map((step, index) => (
              <div key={step} className="flex items-center flex-1">
                <div className="flex flex-col items-center justify-center text-center flex-1">
                  <div
                    className={`w-10 h-10 flex items-center justify-center rounded-full font-bold transition-all duration-300 ${
                      currentStep >= step
                        ? 'bg-gradient-to-r from-blue-600 to-green-600 text-white'
                        : 'bg-gray-200 text-gray-500'
                    }`}
                  >
                    {step}
                  </div>
                  <span className="text-sm text-gray-600 mt-2">
                    {step === 1 ? '基本情報' : step === 2 ? '血液データ' : '確認・同意'}
                  </span>
                </div>
                {step < 3 && (
                  <div
                    className={`h-1 mx-2 transition-all duration-300 ${
                      currentStep > step ? 'bg-gradient-to-r from-blue-600 to-green-600' : 'bg-gray-200'
                    }`}
                    style={{ width: '60px' }}
                  ></div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Step 1: 基本情報 */}
        {currentStep === 1 && (
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
            className="bg-white rounded-2xl p-8 shadow-lg"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-6">基本情報の入力</h2>
            <p className="text-sm text-gray-600 mb-8 leading-relaxed">
              ※ 氏名・住所・生年月日は取得しません。匿名でご利用いただけます。
            </p>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
                  年齢（歳）<span className="text-red-500">*</span>
                  <div className="group relative">
                    <i className="ri-question-line text-blue-600 text-base cursor-pointer"></i>
                    <div className="absolute left-0 top-6 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                      年齢は肝機能指標の計算に必要な項目です。
                    </div>
                  </div>
                </label>
                <input
                  type="number"
                  value={age}
                  onChange={(e) => setAge(e.target.value)}
                  placeholder="例: 45（半角数字で入力）"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none transition-colors text-base"
                />
              </div>

              <div>
                <label className="notranslate block text-sm font-bold text-gray-800 mb-3" translate="no">
                  性別 <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={() => setGender('male')}
                    className={`px-6 py-4 border-2 rounded-lg font-semibold transition-all duration-300 cursor-pointer ${
                      gender === 'male'
                        ? 'border-blue-600 bg-blue-50 text-blue-700'
                        : 'border-gray-200 text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    男性
                  </button>
                  <button
                    onClick={() => setGender('female')}
                    className={`px-6 py-4 border-2 rounded-lg font-semibold transition-all duration-300 cursor-pointer ${
                      gender === 'female'
                        ? 'border-blue-600 bg-blue-50 text-blue-700'
                        : 'border-gray-200 text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    女性
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
                  体重（kg）<span className="text-red-500">*</span>
                  <div className="group relative">
                    <i className="ri-question-line text-blue-600 text-base cursor-pointer"></i>
                    <div className="absolute left-0 top-6 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                      概算で構いません。参考情報として使用します。
                    </div>
                  </div>
                </label>
                <input
                  type="number"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  placeholder="例: 65（半角数字で入力）"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none transition-colors text-base"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-800 mb-3">
                  飲酒習慣 <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-3 gap-4">
                  <button
                    onClick={() => setAlcohol('none')}
                    className={`px-4 py-4 border-2 rounded-lg font-semibold transition-all duration-300 cursor-pointer text-sm ${
                      alcohol === 'none'
                        ? 'border-blue-600 bg-blue-50 text-blue-700'
                        : 'border-gray-200 text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    なし
                  </button>
                  <button
                    onClick={() => setAlcohol('sometimes')}
                    className={`px-4 py-4 border-2 rounded-lg font-semibold transition-all duration-300 cursor-pointer text-sm ${
                      alcohol === 'sometimes'
                        ? 'border-blue-600 bg-blue-50 text-blue-700'
                        : 'border-gray-200 text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    時々
                  </button>
                  <button
                    onClick={() => setAlcohol('daily')}
                    className={`px-4 py-4 border-2 rounded-lg font-semibold transition-all duration-300 cursor-pointer text-sm ${
                      alcohol === 'daily'
                        ? 'border-blue-600 bg-blue-50 text-blue-700'
                        : 'border-gray-200 text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    毎日
                  </button>
                </div>
              </div>
            </div>

            <div className="flex gap-4 mt-8">
              <button
                onClick={() => navigate('/')}
                className="px-8 py-4 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer"
              >
                キャンセル
              </button>
              <button
                onClick={handleNext}
                disabled={!isStep1Valid}
                className={`flex-1 px-8 py-4 bg-gradient-to-r from-blue-600 to-green-600 text-white font-bold rounded-lg transition-all duration-300 shadow-lg whitespace-nowrap cursor-pointer ${
                  !isStep1Valid ? 'opacity-50 cursor-not-allowed' : 'hover:from-blue-700 hover:to-green-700'
                }`}
              >
                次へ
              </button>
            </div>
          </motion.div>
        )}

        {/* Step 2: 血液データ */}
        {currentStep === 2 && (
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
            className="bg-white rounded-2xl p-8 shadow-lg"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-6">血液データの入力</h2>
            <p className="text-sm text-gray-600 mb-8 leading-relaxed">
              健康診断や血液検査の結果をご確認の上、入力してください。
            </p>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
                  AST（GOT）（IU/L）<span className="text-red-500">*</span>
                  <div className="group relative">
                    <i className="ri-question-line text-blue-600 text-base cursor-pointer"></i>
                    <div className="absolute left-0 top-6 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                      肝細胞の状態を示す酵素です。血液検査結果に記載されています。
                    </div>
                  </div>
                </label>
                <input
                  type="number"
                  value={ast}
                  onChange={(e) => setAst(e.target.value)}
                  placeholder="例: 25（半角数字で入力）"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none transition-colors text-base"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
                  ALT（GPT）（IU/L）<span className="text-red-500">*</span>
                  <div className="group relative">
                    <i className="ri-question-line text-blue-600 text-base cursor-pointer"></i>
                    <div className="absolute left-0 top-6 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                      肝細胞の状態を示す酵素です。血液検査結果に記載されています。
                    </div>
                  </div>
                </label>
                <input
                  type="number"
                  value={alt}
                  onChange={(e) => setAlt(e.target.value)}
                  placeholder="例: 30（半角数字で入力）"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none transition-colors text-base"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
                  血小板数（万/μL）<span className="text-red-500">*</span>
                  <div className="group relative">
                    <i className="ri-question-line text-blue-600 text-base cursor-pointer"></i>
                    <div className="absolute left-0 top-6 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                      血液の凝固に関わる成分です。
                    </div>
                  </div>
                </label>
                <input
                  type="number"
                  value={plate}
                  onChange={(e) => setPlate(e.target.value)}
                  placeholder="例: 20.5（半角数字で入力）"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none transition-colors text-base"
                />
                <div className="mt-3 p-3 bg-yellow-50 border-l-4 border-yellow-400 rounded-lg">
                  <div className="flex items-start gap-2">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i className="ri-alert-line text-yellow-600 text-base"></i>
                    </div>
                    <p className="text-xs text-gray-700 leading-relaxed">
                      <strong>⚠️ 単位に注意</strong><br />
                      ※血小板数の基準値は一般的に15〜45万/μLです。
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 p-5 bg-blue-50 border-l-4 border-blue-500 rounded-lg">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-information-line text-blue-600 text-xl"></i>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed">
                  入力されたデータは計算にのみ使用され、個人を特定する情報と紐付けられることはありません。
                </p>
              </div>
            </div>

            <div className="flex gap-4 mt-8">
              <button
                onClick={handleBack}
                className="px-8 py-4 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer"
              >
                戻る
              </button>
              <button
                onClick={handleNext}
                disabled={!isStep2Valid}
                className={`flex-1 px-8 py-4 bg-gradient-to-r from-blue-600 to-green-600 text-white font-bold rounded-lg transition-all duration-300 shadow-lg whitespace-nowrap cursor-pointer ${
                  !isStep2Valid ? 'opacity-50 cursor-not-allowed' : 'hover:from-blue-700 hover:to-green-700'
                }`}
              >
                次へ
              </button>
            </div>
          </motion.div>
        )}

        {/* Step 3: 確認・同意 */}
        {currentStep === 3 && (
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
            className="bg-white rounded-2xl p-8 shadow-lg"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-6">入力内容の確認</h2>

            <div className="space-y-6 mb-8">
              <div className="p-5 bg-gray-50 rounded-lg">
                <h3 className="text-base font-bold text-gray-900 mb-4">基本情報</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">年齢</span>
                    <span className="font-semibold text-gray-900">{age}歳</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">性別</span>
                    <span className="font-semibold text-gray-900">{gender === 'male' ? '男性' : '女性'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">体重</span>
                    <span className="font-semibold text-gray-900">{weight}kg</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">飲酒習慣</span>
                    <span className="font-semibold text-gray-900">
                      {alcohol === 'none' ? 'なし' : alcohol === 'sometimes' ? '時々' : '毎日'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="p-5 bg-gray-50 rounded-lg">
                <h3 className="text-base font-bold text-gray-900 mb-4">血液データ</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">AST（GOT）</span>
                    <span className="font-semibold text-gray-900">{ast} IU/L</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">ALT（GPT）</span>
                    <span className="font-semibold text-gray-900">{alt} IU/L</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">血小板数</span>
                    <span className="font-semibold text-gray-900">{plate} 万/μL</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-8 p-5 bg-yellow-50 border-l-4 border-yellow-500 rounded-lg">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-alert-line text-yellow-600 text-xl"></i>
                </div>
                <div className="text-sm text-gray-700 leading-relaxed">
                  <p className="font-bold mb-2">重要な注意事項</p>
                  <ul className="space-y-1">
                    <li>• この結果は診断ではありません</li>
                    <li>• 医療判断・治療の代わりになるものではありません</li>
                    <li>• 体調不良や不安がある場合は、必ず医師・医療機関にご相談ください</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="mb-6 p-5 bg-blue-50 rounded-lg border-2 border-blue-200">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                />
                <span className="text-sm text-gray-800 leading-relaxed">
                  <strong>利用規約・注意事項に同意する</strong>
                  <br />
                  上記の注意事項を理解し、本サービスが医療行為・診断を行うものではないことに同意します。
                </span>
              </label>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <div className="flex gap-4">
              <button
                onClick={handleBack}
                disabled={isSubmitting}
                className="px-8 py-4 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                戻る
              </button>
              <button
                onClick={handleSubmit}
                disabled={!consent || isSubmitting}
                className={`flex-1 px-8 py-4 bg-gradient-to-r from-blue-600 to-green-600 text-white font-bold rounded-lg transition-all duration-300 shadow-lg whitespace-nowrap cursor-pointer ${
                  !consent || isSubmitting ? 'opacity-50 cursor-not-allowed' : 'hover:from-blue-700 hover:to-green-700'
                }`}
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    送信中...
                  </span>
                ) : (
                  '肝臓の状態をチェックする'
                )}
              </button>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default CheckPage;
